import {Vec2} from "./math/vec2";
import {ClientDiagramConnection, ClientDiagramConnectionAnchor, ClientDiagramNode} from "./types";
import {DiagramNodeId} from "platformed-client/types";

/**
 * Calculates the grid cells that a connection passes through between two points.
 */
function generateConnectionPathSegment(start: Vec2, end: Vec2): Vec2[] {
  const cells: Vec2[] = [];

  // Determine the diagonal direction
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  // How far can we go diagonally?
  const diagonalDistance = Math.min(Math.abs(dx), Math.abs(dy));
  const diagonalStepX = Math.sign(dx);
  const diagonalStepY = Math.sign(dy);

  // Add cells along the diagonal path
  let currentX = start.x;
  let currentY = start.y;

  cells.push(new Vec2(currentX, currentY));

  for (let i = 0; i < diagonalDistance; i++) {
    currentX += diagonalStepX;
    currentY += diagonalStepY;
    cells.push(new Vec2(currentX, currentY));
  }

  // Now move orthogonally to the end point
  // Determine which axis needs more movement
  const remainingX = end.x - currentX;
  const remainingY = end.y - currentY;

  // Move horizontally if needed
  if (remainingX !== 0) {
    const stepX = Math.sign(remainingX);
    for (let i = 0; i < Math.abs(remainingX); i++) {
      currentX += stepX;
      cells.push(new Vec2(currentX, currentY));
    }
  }

  // Move vertically if needed
  if (remainingY !== 0) {
    const stepY = Math.sign(remainingY);
    for (let i = 0; i < Math.abs(remainingY); i++) {
      currentY += stepY;
      cells.push(new Vec2(currentX, currentY));
    }
  }

  return cells;
}

/**
 * Calculates the grid cells that a connection passes through using waypoints.
 */
export function generateConnectionPath(waypoints: Vec2[]): Vec2[] {
  if (waypoints.length < 2) {
    return waypoints.slice();
  }

  const cells: Vec2[] = [];

  for (let i = 0; i < waypoints.length - 1; i++) {
    const segmentCells = generateConnectionPathSegment(waypoints[i], waypoints[i + 1]);

    // Add all cells from this segment, but avoid duplicating the start point
    // (except for the very first segment)
    if (i === 0) {
      cells.push(...segmentCells);
    } else {
      // Skip the first cell of subsequent segments to avoid duplication
      cells.push(...segmentCells.slice(1));
    }
  }

  return cells;
}

export function getPathMidPoint(path: Vec2[]): Vec2 {
  const midIndex = Math.floor((path.length - 1) / 2);
  return path[midIndex];
}

/**
 * Creates waypoints array from a connection and nodes: start -> anchors -> end
 */
export function getConnectionWaypoints(
  connection: ClientDiagramConnection,
  nodes: ReadonlyMap<DiagramNodeId, ClientDiagramNode>,
): Vec2[] {
  const fromNode = nodes.get(connection.from);
  const toNode = nodes.get(connection.to);

  if (!fromNode || !toNode) {
    throw new Error("Cannot create waypoints: from or to node does not exist.");
  }

  return [fromNode.position, ...connection.anchors.map(anchor => anchor.position), toNode.position];
}

/**
 * Merge anchor points that are adjacent to eachother. First / last anchor
 * points that are the same as the connection start / end are also removed.
 **/
export function removeRedundantAnchors(
  connection: ClientDiagramConnection,
  nodes: ReadonlyMap<DiagramNodeId, ClientDiagramNode>,
): ClientDiagramConnectionAnchor[] {
  const fromNode = nodes.get(connection.from);
  const toNode = nodes.get(connection.to);

  if (!fromNode || !toNode) {
    throw new Error("From or to node must exist.");
  }

  // First, dedup adjacent anchors.
  let result = connection.anchors.filter((anchor, index, arr) => {
    // Always keep the first element
    if (index === 0) return true;

    const prev = arr[index - 1];
    return !prev.position.equals(anchor.position);
  });

  // Next, remove start and end anchors that match start and end of the connection.
  if (result.length > 0 && result[0].position.equals(fromNode.position)) {
    result = result.slice(1);
  }

  if (result.length > 0 && result[result.length - 1].position.equals(toNode.position)) {
    result = result.slice(0, -1);
  }

  return result;
}
